import dayjs from "dayjs"
import { get, post } from "../api/axios"
import endpoints from "../api/endpoints"
import { setNotificationInbox } from "../store/slices/notificationsSlice"
import { maybeCelebrateLevelUpFromInbox } from "./xpLevelCelebration"
import {
  DM_CONTEXT_QUERY_KEY,
  DM_QUERY_KEY,
} from "../components/DirectChat"

export const DROPDOWN_UNREAD_LIMIT = 10

/** Listing tabs — API filter `is_read`: 0 = unread, 1 = read. */
export const NOTIFICATION_LIST_TABS = [
  { key: "unread", label: "Unread", isRead: 0 },
  { key: "read", label: "Read", isRead: 1 },
]

export const DEFAULT_NOTIFICATION_LIST_TAB = "unread"

export const getNotificationListTab = key =>
  NOTIFICATION_LIST_TABS.find(tab => tab.key === key) ||
  NOTIFICATION_LIST_TABS[0]

const formatNotificationDate = value => {
  const date = dayjs(value)
  return date.isValid() ? date.format("MMM DD, YYYY h:mm A") : "—"
}

export const mapNotification = (n = {}) => {
  const data = n.data && typeof n.data === "object" ? n.data : {}
  const meta = data.meta && typeof data.meta === "object" ? data.meta : {}
  const sessionId = data.session_id || meta.session_id || ""

  return {
    id: n.id,
    title: data.title || "",
    message: data.message || data.body || data.title || "",
    url: data.url || "",
    time: formatNotificationDate(n.created_at),
    createdAt: n.created_at,
    read: Boolean(n.read_at),
    sessionId: sessionId ? String(sessionId) : "",
    metaType: meta.type ? String(meta.type) : "",
    meta,
    data,
  }
}

export const getNotificationPath = url => {
  const raw = String(url || "").trim()
  if (!raw) return ""

  if (raw.startsWith("/")) return raw

  try {
    const parsed = new URL(raw, window.location.origin)
    const appHost = window.location.hostname
    const isAppHost =
      parsed.hostname === appHost ||
      parsed.hostname === "amok.earth" ||
      parsed.hostname.endsWith(".amok.earth")
    if (isAppHost) {
      return `${parsed.pathname}${parsed.search}${parsed.hash}` || "/"
    }
    return parsed.href
  } catch {
    return raw
  }
}

/** Pull dm / context_id from a notification path or absolute URL. */
export const getDirectMessageQueryFromUrl = url => {
  const path = getNotificationPath(url)
  if (!path || path.startsWith("http://") || path.startsWith("https://")) {
    return null
  }

  try {
    const parsed = new URL(path, window.location.origin)
    const dm = String(parsed.searchParams.get(DM_QUERY_KEY) || "").trim()
    if (!dm) return null

    const contextId = String(
      parsed.searchParams.get(DM_CONTEXT_QUERY_KEY) || "",
    ).trim()
    const params = new URLSearchParams()
    params.set(DM_QUERY_KEY, dm)
    if (contextId) params.set(DM_CONTEXT_QUERY_KEY, contextId)
    return `?${params.toString()}`
  } catch {
    return null
  }
}

export const getNotificationSessionId = notification => {
  const data =
    notification?.data && typeof notification.data === "object"
      ? notification.data
      : {}
  const meta =
    notification?.meta && typeof notification.meta === "object"
      ? notification.meta
      : data.meta && typeof data.meta === "object"
        ? data.meta
        : {}

  return String(
    notification?.sessionId || data.session_id || meta.session_id || "",
  ).trim()
}

const SESSION_JOIN_BLOCKED_META_TYPES = new Set([
  "session.collaborator.reacted",
  "message.received",
  "task_collaborator.added",
  "task_series_collaborator.added",
  "project.invitation",
])

export const isSessionJoinInviteNotification = notification => {
  const sessionId = getNotificationSessionId(notification)
  if (!sessionId) return false

  const metaType = String(notification?.metaType || "").toLowerCase()
  if (SESSION_JOIN_BLOCKED_META_TYPES.has(metaType)) return false

  const path = getNotificationPath(notification?.url || "")
  if (!path.includes("/sessions")) return false
  if (/\/sessions\/[^/?#]+/.test(path)) return false

  const title = String(notification?.title || "").toLowerCase()
  if (
    title.includes("please join") ||
    title.includes("created a new session")
  ) {
    return true
  }

  if (
    !metaType ||
    metaType === "session.created" ||
    metaType === "session.invited"
  ) {
    return true
  }

  return false
}

export const openNotificationUrl = (url, navigate, options = {}) => {
  const path = getNotificationPath(url)
  if (!path) return

  if (path.startsWith("http://") || path.startsWith("https://")) {
    window.location.assign(path)
    return
  }

  // DM deep link: stay on the current screen; only apply dm (+ context_id).
  // Backend may send /dashboard?dm=… — path is ignored for floating chat.
  const dmSearch = getDirectMessageQueryFromUrl(path)
  if (dmSearch && typeof window !== "undefined") {
    navigate(
      {
        pathname: window.location.pathname || "/",
        search: dmSearch,
        hash: window.location.hash || "",
      },
      { replace: true, ...options },
    )
    return
  }

  navigate(path, options)
}

export const openNotification = (notification, navigate) => {
  if (!notification) return

  const path = getNotificationPath(notification.url || "")
  if (!path) return

  if (isSessionJoinInviteNotification(notification) && navigate) {
    openNotificationUrl(notification.url, navigate, {
      state: {
        openJoinSessionId: getNotificationSessionId(notification),
      },
    })
    return
  }

  openNotificationUrl(notification.url, navigate)
}

const isCount = value => {
  if (typeof value === "number") {
    return Number.isFinite(value) && value >= 0 && value < 1_000_000
  }
  if (typeof value === "string" && /^\d+$/.test(value.trim())) {
    const parsed = Number(value.trim())
    return Number.isFinite(parsed) && parsed < 1_000_000
  }
  return false
}

const toCount = value => (isCount(value) ? Math.floor(Number(value)) : null)

const extractList = result => {
  if (Array.isArray(result?.data)) return result.data
  if (Array.isArray(result)) return result
  return []
}

export const fetchNotificationCount = async () => {
  const response = await get(endpoints.getNotificationCount)
  if (!response?.data?.success) {
    return { total: 0, unread: 0, response }
  }

  const result = response.data.result ?? response.data.data ?? {}
  const payload =
    result && typeof result === "object" && !Array.isArray(result) ? result : {}

  const unread =
    toCount(payload.unread_count) ??
    toCount(payload.unreadCount) ??
    toCount(payload.unread) ??
    toCount(payload.count) ??
    toCount(typeof result === "number" ? result : null) ??
    0

  const total =
    toCount(payload.total_count) ??
    toCount(payload.totalCount) ??
    toCount(payload.total) ??
    unread

  return { total, unread, response }
}

export const fetchLatestUnreadNotifications = async (
  limit = DROPDOWN_UNREAD_LIMIT,
) => {
  const response = await get(endpoints.getNotifications, {
    params: {
      is_read: 0,
      page: 1,
      page_size: limit,
      per_page: limit,
    },
  })

  if (!response?.data?.success) {
    return { items: [], unreadTotal: 0, response }
  }

  const result = response.data.result || {}
  const unreadRows = extractList(result)
    .filter(item => !item.read_at)
    .sort((a, b) => {
      const aTime = new Date(a.created_at).getTime() || 0
      const bTime = new Date(b.created_at).getTime() || 0
      return bTime - aTime
    })
  const items = unreadRows.slice(0, limit).map(mapNotification)

  const reportedTotal =
    toCount(result.unread_count) ??
    toCount(result.unreadCount) ??
    toCount(result.total)

  // A short page of unread rows is the full unread set. Don't use a
  // paginator `total` that still counts read notifications.
  const unreadTotal =
    items.length < limit ? items.length : reportedTotal ?? items.length

  return { items, unreadTotal, response }
}

export const markAllNotificationsRead = () =>
  post(endpoints.markNotificationsRead, { read_all: true })

export const markNotificationsReadByIds = ids =>
  post(endpoints.markNotificationsRead, { ids })

export const resolveUnreadInbox = async () => {
  const [listResult, counts] = await Promise.all([
    fetchLatestUnreadNotifications(DROPDOWN_UNREAD_LIMIT),
    fetchNotificationCount(),
  ])

  const items = listResult.items
  let unreadCount = Math.max(listResult.unreadTotal, items.length)
  if (items.length === DROPDOWN_UNREAD_LIMIT && counts.unread > unreadCount) {
    unreadCount = counts.unread
  }

  return { items, unreadCount }
}

let inboxSyncPromise = null

export const refreshNotificationInbox = async (dispatch, options = {}) => {
  if (!dispatch) return null
  if (inboxSyncPromise) return inboxSyncPromise

  inboxSyncPromise = (async () => {
    try {
      const inbox = await resolveUnreadInbox()
      dispatch(setNotificationInbox(inbox))
      if (!options.skipLevelUpCelebration) {
        maybeCelebrateLevelUpFromInbox(inbox?.items, {
          userId: options.userId,
          silent: Boolean(options.silentLevelUp),
        })
      }
      return inbox
    } finally {
      inboxSyncPromise = null
    }
  })()

  return inboxSyncPromise
}
